import React, { createContext, useContext, useEffect, useState } from "react";
import { makeNamespace, storageGet, storageSet } from "../utils/storage";

export type ExampleLanguage = "el" | "en";

interface ExampleLanguageContextType {
    exampleLanguage: ExampleLanguage;
    toggleExampleLanguage: () => void;
    setExampleLanguage: (language: ExampleLanguage) => void;
}

const ExampleLanguageContext = createContext<ExampleLanguageContextType | undefined>(undefined);

const STORAGE_SCHEMA_V = 1;
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

const deriveUserId = (): string => {
    try {
        const cfg = (window as any).PublicWidgetConfig;
        return cfg?.userId || "default";
    } catch {
        return "default";
    }
};

const getStorageKey = (): string => {
    const ns = makeNamespace(window.location.origin, deriveUserId(), STORAGE_SCHEMA_V);
    return ns.key("example_language");
};

const deriveInitialExampleLanguage = (): ExampleLanguage => {
    try {
        const stored = storageGet<ExampleLanguage>(getStorageKey());
        if (stored === "el" || stored === "en") {
            return stored;
        }
    } catch {}

    return "el";
};

export const ExampleLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [exampleLanguage, setExampleLanguage] = useState<ExampleLanguage>(deriveInitialExampleLanguage);

    useEffect(() => {
        try {
            storageSet(getStorageKey(), exampleLanguage, { schemaVersion: STORAGE_SCHEMA_V, ttlMs: STORAGE_TTL_MS });
        } catch {}
    }, [exampleLanguage]);

    const toggleExampleLanguage = () => {
        setExampleLanguage(current => (current === "el" ? "en" : "el"));
    };

    return (
        <ExampleLanguageContext.Provider value={{ exampleLanguage, toggleExampleLanguage, setExampleLanguage }}>
            {children}
        </ExampleLanguageContext.Provider>
    );
};

export const useExampleLanguage = () => {
    const context = useContext(ExampleLanguageContext);
    if (!context) {
        throw new Error("useExampleLanguage must be used within an ExampleLanguageProvider");
    }
    return context;
};
